/**
 * NBMCarousel — Generic scroll-snap carousel with no external dependencies.
 *
 * Features:
 *   - Horizontal scroll-snap based sliding
 *   - Optional auto-scroll with configurable interval
 *   - Dot pagination (auto-generated)
 *   - Touch / swipe via native scroll-snap
 *   - Pause on hover and touch
 *   - Public methods: goTo, next, prev, destroy
 *
 * HTML contract:
 *   <div data-carousel data-carousel-autoplay="true" data-carousel-interval="5000">
 *     <div class="carousel__track">       ← scrollable flex container
 *       <div class="carousel__item">…</div>
 *       <div class="carousel__item">…</div>
 *     </div>
 *     <!-- dots and nav are generated automatically -->
 *   </div>
 *
 * CSS expectations (minimal required styles injected at runtime):
 *   .carousel__track  — overflow-x: auto; scroll-snap-type: x mandatory; display: flex;
 *   .carousel__item   — scroll-snap-align: start; flex: 0 0 100%;
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  Utility: inject base CSS once                                      */
  /* ------------------------------------------------------------------ */

  let cssInjected = false;

  function injectBaseCSS() {
    if (cssInjected) return;
    cssInjected = true;

    const style = document.createElement('style');
    style.textContent = [
      '[data-carousel] { position: relative; }',
      '.carousel__track {',
      '  display: flex;',
      '  overflow-x: auto;',
      '  scroll-snap-type: x mandatory;',
      '  -webkit-overflow-scrolling: touch;',
      '  scrollbar-width: none;',
      '}',
      '.carousel__track::-webkit-scrollbar { display: none; }',
      '.carousel__item {',
      '  flex: 0 0 100%;',
      '  scroll-snap-align: start;',
      '}',
      '.carousel__dots {',
      '  display: flex;',
      '  justify-content: center;',
      '  align-items: center;',
      '  gap: 4px;',
      '  padding: 12px 0;',
      '}',
      '.carousel__dot {',
      '  width: 6px; height: 6px;',
      '  border-radius: 50%;',
      '  border: none;',
      '  background: var(--color-border-dark, rgba(0,0,0,.2));',
      '  cursor: pointer;',
      '  padding: 0;',
      '  transition: background .2s ease, width .2s ease;',
      '}',
      '.carousel__counter {',
      '  text-align: center;',
      '  padding: 10px 0;',
      '  font-size: 12px;',
      '  letter-spacing: 0.08em;',
      '  color: var(--color-text-tertiary, rgba(0,0,0,.4));',
      '  font-variant-numeric: tabular-nums;',
      '}',
      '.carousel__dot.is-active {',
      '  background: var(--color-text-primary, rgba(0,0,0,.8));',
      '}'
    ].join('\n');
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------------------ */
  /*  NBMCarousel Class                                                  */
  /* ------------------------------------------------------------------ */

  class NBMCarousel {
    /**
     * @param {HTMLElement} container — The [data-carousel] wrapper element.
     */
    constructor(container) {
      if (!container) {
        console.warn('[NBMCarousel] No container element provided.');
        return;
      }

      injectBaseCSS();

      this.container = container;
      this.track = container.querySelector('.carousel__track');

      if (!this.track) {
        console.warn('[NBMCarousel] No .carousel__track found inside container.');
        return;
      }

      this.slides = this.track.querySelectorAll('.carousel__item');
      this.slideCount = this.slides.length;

      if (this.slideCount === 0) return;

      this.currentIndex = 0;
      this.autoplayEnabled = container.getAttribute('data-carousel-autoplay') === 'true';
      this.interval = parseInt(container.getAttribute('data-carousel-interval'), 10) || 5000;
      this.autoplayTimer = null;
      this.destroyed = false;

      // Bound handlers (stored for removal on destroy)
      this._onScroll = this._handleScroll.bind(this);
      this._onMouseEnter = this._pause.bind(this);
      this._onMouseLeave = this._resume.bind(this);
      this._onTouchStart = this._pause.bind(this);
      this._onTouchEnd = this._resume.bind(this);

      this._buildDots();
      this._bindEvents();

      if (this.autoplayEnabled) this._startAutoplay();
    }

    /* ---- Dot pagination ------------------------------------------------ */

    _buildDots() {
      // Remove any existing dots/counter
      const existing = this.container.querySelector('.carousel__dots, .carousel__counter');
      if (existing) existing.parentNode.removeChild(existing);

      if (this.slideCount <= 1) {
        this.dots = [];
        this._counterEl = null;
        return;
      }

      // Many slides → show a compact counter instead of individual dots
      if (this.slideCount > 7) {
        const counter = document.createElement('div');
        counter.className = 'carousel__counter';
        counter.setAttribute('aria-live', 'polite');
        this._counterEl = counter;
        this._updateCounter();
        this.container.appendChild(counter);
        this.dots = [];
        return;
      }

      this._counterEl = null;
      const wrapper = document.createElement('div');
      wrapper.className = 'carousel__dots';
      this.dots = [];

      for (let index = 0; index < this.slideCount; index++) {
        const dot = document.createElement('button');
        dot.className = `carousel__dot${index === 0 ? ' is-active' : ''}`;
        dot.setAttribute('type', 'button');
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.dataset.index = index;
        wrapper.appendChild(dot);
        this.dots.push(dot);
      }

      wrapper.addEventListener('click', (event) => {
        const dotEl = event.target.closest('.carousel__dot');
        if (dotEl && dotEl.dataset.index !== undefined) {
          this.goTo(parseInt(dotEl.dataset.index, 10));
        }
      });

      this.container.appendChild(wrapper);
    }

    _updateCounter() {
      if (this._counterEl) {
        this._counterEl.textContent = `${this.currentIndex + 1} / ${this.slideCount}`;
      }
    }

    _updateDots() {
      this._updateCounter();
      for (let index = 0; index < this.dots.length; index++) {
        if (index === this.currentIndex) {
          this.dots[index].classList.add('is-active');
        } else {
          this.dots[index].classList.remove('is-active');
        }
      }
    }

    /* ---- Scroll tracking ----------------------------------------------- */

    _handleScroll() {
      if (this.destroyed) return;

      // Determine which slide is most visible based on scroll position
      const scrollLeft = this.track.scrollLeft;
      const slideWidth = this.track.offsetWidth;

      if (slideWidth === 0) return;

      let index = Math.round(scrollLeft / slideWidth);
      index = Math.max(0, Math.min(index, this.slideCount - 1));

      if (index !== this.currentIndex) {
        this.currentIndex = index;
        this._updateDots();
      }
    }

    /* ---- Event binding ------------------------------------------------- */

    _bindEvents() {
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // Pause autoplay on hover / touch
      this.container.addEventListener('mouseenter', this._onMouseEnter);
      this.container.addEventListener('mouseleave', this._onMouseLeave);
      this.container.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.container.addEventListener('touchend', this._onTouchEnd, { passive: true });
    }

    /* ---- Autoplay ------------------------------------------------------ */

    _startAutoplay() {
      if (this.destroyed || this.slideCount <= 1) return;
      this.autoplayTimer = setInterval(() => {
        this.next();
      }, this.interval);
    }

    _pause() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    _resume() {
      if (this.autoplayEnabled && !this.autoplayTimer && !this.destroyed) {
        this._startAutoplay();
      }
    }

    /* ---- Public methods ------------------------------------------------ */

    /**
     * Scroll to a specific slide by index.
     * @param {number} index — Zero-based slide index.
     */
    goTo(index) {
      if (this.destroyed) return;
      if (index < 0) index = this.slideCount - 1;
      if (index >= this.slideCount) index = 0;

      this.currentIndex = index;

      this.track.scrollTo({
        left: this.slides[index].offsetLeft,
        behavior: 'smooth'
      });

      this._updateDots();
    }

    /** Advance to the next slide (wraps around). */
    next() {
      this.goTo(this.currentIndex + 1);
    }

    /** Go to the previous slide (wraps around). */
    prev() {
      this.goTo(this.currentIndex - 1);
    }

    /** Tear down event listeners and stop autoplay. */
    destroy() {
      this.destroyed = true;
      this._pause();
      this.track.removeEventListener('scroll', this._onScroll);
      this.container.removeEventListener('mouseenter', this._onMouseEnter);
      this.container.removeEventListener('mouseleave', this._onMouseLeave);
      this.container.removeEventListener('touchstart', this._onTouchStart);
      this.container.removeEventListener('touchend', this._onTouchEnd);

      const dotsWrapper = this.container.querySelector('.carousel__dots');
      if (dotsWrapper) dotsWrapper.parentNode.removeChild(dotsWrapper);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Auto-init all carousels on the page                                */
  /* ------------------------------------------------------------------ */

  function initAll() {
    const containers = document.querySelectorAll('[data-carousel]');
    for (const container of containers) {
      // Avoid double-init
      if (!container._nbmCarousel) {
        container._nbmCarousel = new NBMCarousel(container);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ------------------------------------------------------------------ */
  /*  NBMArrowCarousel — lightweight prev/next arrow carousel            */
  /*                                                                     */
  /*  Usage:                                                             */
  /*    var c = NBMArrowCarousel(wrapElement);                           */
  /*    c.refresh(); // recalculate arrow disabled state (call after     */
  /*                 // showing a previously hidden container)           */
  /*                                                                     */
  /*  HTML contract:                                                     */
  /*    <div class="…-wrap">          ← pass this element               */
  /*      <div class="carousel__track">…</div>                          */
  /*      <button class="rv-arrow rv-arrow--prev">…</button>            */
  /*      <button class="rv-arrow rv-arrow--next">…</button>            */
  /*    </div>                                                           */
  /* ------------------------------------------------------------------ */

  function NBMArrowCarousel(wrap) {
    var track   = wrap.querySelector('.carousel__track');
    var prevBtn = wrap.querySelector('.rv-arrow--prev');
    var nextBtn = wrap.querySelector('.rv-arrow--next');
    if (!track || !prevBtn || !nextBtn) return { refresh: function () {} };

    function snapOrigin() {
      var first = track.querySelector('.carousel__item');
      return first ? first.offsetLeft : 0;
    }

    function refresh() {
      var snap = snapOrigin();
      prevBtn.disabled = track.scrollLeft <= snap + 4;
      nextBtn.disabled = track.scrollLeft + track.offsetWidth >= track.scrollWidth - snap - 4;
    }

    prevBtn.addEventListener('click', function () {
      track.scrollBy({ left: -track.offsetWidth, behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function () {
      track.scrollBy({ left: track.offsetWidth, behavior: 'smooth' });
    });
    track.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);

    return { refresh: refresh };
  }

  /* ------------------------------------------------------------------ */
  /*  Export                                                              */
  /* ------------------------------------------------------------------ */

  window.NBMCarousel = NBMCarousel;
  window.NBMArrowCarousel = NBMArrowCarousel;
})();
